// useOrderPrint.tsx
import { useState } from "react";
import { LOGO_URL } from "../constants/constans";
import { toast } from "react-toastify";

// Base interfaces for order print
interface CustomItem {
  displayName: string;
  quantity: number;
  unitPrice: number;
  productId?: string;
  tinyId?: string; 
  isAddOn?: boolean; 
}

interface OrderItem {
  menuItemId: string;
  displayName: string;
  itemType: string;
  quantity: number;
  unitPrice: number;
  unitTotalPrice?: number;
  portionSize?: string;
  customItems?: CustomItem[];
  perItemManualDiscount?: number;
  tinyId?: string; // Added tinyId for main items
}

interface Customer {
  id: string;
  displayName: string;
  userId?: string;
  phoneNumber?: string;
  email?: string;
}

interface Cart {
  cartId: string;
  displayName: string;
  cartPersonId?: string;
  userId?: string;
  cartPersonName?: string;
}

interface PaymentSummary {
  totalPrice: number;
  deliveryFee: number;
  discount: number;
  netAmount: number;
  walletPaymentAmount: number;
  cardPaymentAmount: number;
  cashPaymentAmount: number;
  cartCommission: number;
  isDeliveryFree: boolean;
  isManualDiscount: boolean;
  manualDiscountType?: string;
  manualDiscountAmount?: number;
}

export interface Order {
  id: string;
  orderStatus: string;
  orderReference: string;
  totalPrice: number;
  paymentMethod: string;
  phoneNumber?: string;
  displayName: string;
  customer?: Customer;
  cart?: Cart;
  orderItems: OrderItem[];
  paymentSummary?: PaymentSummary;
  createdAt: any;
  deliverAt?: any;
  isDeliveryOrder: boolean;
  isScheduledOrder: boolean;
  paymentCompleted: boolean;
  address?: string;
  orderCreatedBy: string;
  deliveryTimeSlot?: string;
  orderNote?: string;
  packingNote?: string;
  paymentNote?: string;
  orderTakenBy?: {
    displayName: string;
  };
  orderSource?: string;
  organization?: {
    displayName: string;
    organizationId: string;
    phoneNumber?: string;
  };
  promoCode?: string;
  verificationCode?: number;
  tinyId?: string;
   channel?: string; 
  deliveryDetails?: {
    address?: string;
    deliveryInstructions?: string;
    deliveryContactName?: string;
    deliveryContactPhoneNumber?: string;
    coordinates?: {
      latitude?: number;
      longitude?: number;
    };
  };
}

interface UseOrderPrintProps {
  orders?: Order[];
}

export interface PrintByOrderOptions {
  orderId: string;
  orderReference: string;
  copyType: "rider" | "customer";
}

export const useOrderPrint = ({ orders = [] }: UseOrderPrintProps = {}) => {
  const [isPrinting, setIsPrinting] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewHTML, setPreviewHTML] = useState("");

  // Get customer name based on order type
  const getCustomerName = (order: Order): string => {
  if (
      order.orderCreatedBy?.toLowerCase() === "customer" &&
      order.customer?.displayName
    ) {
      return order.customer.displayName;
    } else if (
      order.orderCreatedBy?.toLowerCase() === "cartperson" &&
      order.cart?.displayName
    ) {
      return order.cart.displayName;
    } else if (order.orderCreatedBy?.toLowerCase() === "restaurant") {
      // Corporate/org orders: prefer organization name, then customer name
      if (order.organization?.displayName) {
        return order.organization.displayName;
      } else if (order.customer?.displayName) {
        return order.customer.displayName;
      }
    }
    return (
      order.organization?.displayName ||
      order.customer?.displayName ||
      order.displayName ||
      "N/A"
    );
  };

  // Get contact number
  const getContactNumber = (order: Order): string => {
    if (
      order.orderCreatedBy?.toLowerCase() === "customer" &&
      order.customer?.phoneNumber
    ) {
      return order.customer.phoneNumber;
    } else if (order.phoneNumber) {
      return order.phoneNumber;
    } else if (order.deliveryDetails?.deliveryContactPhoneNumber) {
      return order.deliveryDetails.deliveryContactPhoneNumber;
    }
    return order.customer?.phoneNumber || "N/A";
  };

  // Get payment method display
  const getPaymentMethodDisplay = (order: Order): string => {
    if (order.promoCode) {
      return "Promo";
    }

    switch (order.paymentMethod) {
      case "CashPlusWallet":
        return "Cash & Wallet";
      case "CardPlusWallet":
        return "Card & Wallet";
      case "BankTransfer":
        return "Bank Transfer";
      default:
        return order.paymentMethod || "Cash";
    }
  };

  // Calculate total per item with discount
  const calculateItemTotal = (item: OrderItem): number => {
    const price = item.unitPrice || 0;
    const quantity = item.quantity || 0;
    const discount = item.perItemManualDiscount || 0;
    return (price - discount) * quantity;
  };

  // Helper function to get tinyId from item or custom item
  const getItemTinyId = (
    item: OrderItem,
    customItem?: CustomItem
  ): string | undefined => {
    // Check custom item first
    if (customItem && customItem.tinyId) {
      return customItem.tinyId;
    }
    // Then check main item
    return item.tinyId;
  };

  // Generate order / packing / payment notes HTML
  // Payment note is shown for rider copy only.
  const generateOrderNotesHTML = (
    order: Order,
    copyType: "rider" | "customer"
  ): string => {
    const notesHTML: string[] = [];

    if (order.orderNote) {
      notesHTML.push(`
        <div class="order-note-section">
          <div class="order-note-label">Order Note :</div>
          <div class="order-note-text">${order.orderNote}</div>
        </div>
      `);
    }

    if (order.packingNote) {
      notesHTML.push(`
        <div class="order-note-section">
          <div class="order-note-label">Packing Note :</div>
          <div class="order-note-text">${order.packingNote}</div>
        </div>
      `);
    }

    if (copyType === "rider" && order.paymentNote) {
      notesHTML.push(`
        <div class="order-note-section payment-note-section">
          <div class="order-note-label">Payment Note :</div>
          <div class="order-note-text">${order.paymentNote}</div>
        </div>
      `);
    }

    return notesHTML.join("");
  };

 // Generate packing list HTML - Modified to show add-on items under each custom menu
const generatePackingListHTML = (
  order: Order,
  copyType: "rider" | "customer" = "rider"
): string => {
  const packingListHTML: string[] = [];

  packingListHTML.push(`
  <div class="packing-section">
    <div class="packing-header">PACKING LIST</div>
    <div class="thin-divider"></div>
`);

  order.orderItems.forEach((item, idx) => {
    const itemTinyId = getItemTinyId(item);
    const tinyIdBadge = itemTinyId ? 
      `<span class="tiny-id">(${itemTinyId})</span>` : "";

    // Add main item with checkbox (only for rider copy)
    if (copyType === "rider") {
      packingListHTML.push(`
      <div class="packing-item">
        <div class="packing-details">
          <div class="packing-name">${idx + 1}. ${item.displayName}
            ${tinyIdBadge} -
            <span class="item-quantity">${item.quantity}</span>
          </div>
        </div>
        <div class="checkbox"></div>
      </div>
    `);
    } else {
      // Customer copy - no checkbox
      packingListHTML.push(`
      <div class="packing-item-no-checkbox">
        <div class="packing-details">
          <div class="packing-name">${idx + 1}. ${item.displayName}
            ${tinyIdBadge} -
            <span class="item-quantity">${item.quantity}</span>
          </div>
        </div>
      </div>
    `);
    }

    // Add custom items if this is a CustomMenu
    if (
      item.itemType === "CustomMenu" &&
      item.customItems &&
      item.customItems.length > 0
    ) {
      // Separate non-addon and add-on items
      const nonAddOnItems = item.customItems.filter(customItem => !customItem.isAddOn);
      const addOnItems = item.customItems.filter(customItem => customItem.isAddOn);
      
      // Show non-addon items first - Only show tinyId if custom item has its own tinyId
      nonAddOnItems.forEach(
        (customItem: CustomItem, customIdx: number) => {
          // Only show tinyId for custom item if it's different from the main item tinyId
          let customTinyIdBadge = "";
          if (customItem.tinyId && customItem.tinyId !== itemTinyId) {
            customTinyIdBadge = `<span class="tiny-id">(${customItem.tinyId})</span>`;
          }
          
          const itemClass =
            copyType === "rider"
              ? "custom-packing-item"
              : "custom-packing-item-no-checkbox";

          packingListHTML.push(`
        <div class="${itemClass}">
          <div class="custom-packing-details">
            <div class="custom-packing-name">* ${customItem.displayName}
              ${customTinyIdBadge}
              <span class="item-quantity">${customItem.quantity}</span>
            </div>
          </div>
        </div>
      `);
        }
      );
      
      // Show add-on items under the same custom menu
      if (addOnItems.length > 0) {
        // Add a separator for add-on items
        packingListHTML.push(`
          <div class="addon-separator">
            <div class="addon-header">ADD ON ITEMS</div>
          </div>
        `);
        
        // Group add-on items by name and tinyId to combine quantities
        const groupedAddOns: Record<string, {quantity: number, tinyId?: string}> = {};
        
        addOnItems.forEach(customItem => {
          // Only use custom item tinyId if it's different from main item tinyId
          const customItemTinyId = customItem.tinyId && customItem.tinyId !== itemTinyId ? customItem.tinyId : undefined;
          const key = customItem.displayName + (customItemTinyId || '');
          if (groupedAddOns[key]) {
            groupedAddOns[key].quantity += customItem.quantity;
          } else {
            groupedAddOns[key] = {
              quantity: customItem.quantity,
              tinyId: customItemTinyId
            };
          }
        });
        
        // Display grouped add-on items
        Object.entries(groupedAddOns).forEach(([name, data], addonIdx) => {
          const displayName = name.replace(data.tinyId || '', '').trim();
          const tinyIdBadge = data.tinyId ? 
            `<span class="tiny-id">(${data.tinyId})</span>` : "";
          const itemClass =
            copyType === "rider"
              ? "custom-packing-item addon-item"
              : "custom-packing-item-no-checkbox addon-item";

          packingListHTML.push(`
          <div class="${itemClass}">
            <div class="custom-packing-details">
              <div class="custom-packing-name">* ${displayName}
                ${tinyIdBadge} -
                <span class="item-quantity">${data.quantity}</span>
              </div>
            </div>
          </div>
        `);
        });
      }
    }

    if (idx < order.orderItems.length - 1) {
      packingListHTML.push(`<div class="dotted-divider"></div>`);
    }
  });

  packingListHTML.push(`</div>`);
  return packingListHTML.join("");
};

  // Generate HTML for specific order
  const generatePrintHTMLForOrder = (
    order: Order,
    orderReference: string,
    copyType: "rider" | "customer" = "rider"
  ): string => {
    const orderListHTML: string[] = [];

    const currentDate = new Date();
    const dateStr = currentDate.toLocaleDateString("en-GB");
    const timeStr = currentDate.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    const customerName = getCustomerName(order);
    const contactNumber = getContactNumber(order);
    const paymentMethod = getPaymentMethodDisplay(order);
    const isOrganizationOrder = !!(order.organization?.displayName);

    if (copyType === "rider") {
      // RIDER COPY - Single order document
      orderListHTML.push(`
        <div class="header">
          <div class="logo-container">
            <img src="${LOGO_URL}" alt="logo" class="logo-image" />
          </div>
          <div class="header-title">ORDER BILL</div>
          <div class="header-subtitle">RIDER COPY</div>
        </div>
        
        <div class="order-ref-with-bg">ORDER REFERENCE : ${orderReference}</div>
        
        <div class="info-section">
          ${
            order.tinyId
              ? `<div class="info-line">Order Tiny ID: <span class="tiny-id-header">(${order.tinyId})</span></div>`
              : ""
          }
          <div class="info-line">Date : ${dateStr}</div>
          <div class="info-line">Time : ${timeStr}</div>
          ${
            order.cart?.displayName
              ? `<div class="info-line">Cart : ${order.cart.displayName}</div>`
              : ""
          }
           ${
      order.channel
        ? `<div class="info-line">Channel : ${order.channel}</div>`
        : ""
    }
          ${
            order.cart?.cartPersonName
              ? `<div class="info-line">Rider Name: ${order.cart.cartPersonName}</div>`
              : ""
          }
        </div>
        
        <div class="divider"></div>
      `);

      // Customer information
      orderListHTML.push(`
        <div class="group-section">
          <div class="group-header">${
            isOrganizationOrder ? "ORGANIZATION" : "CUSTOMER"
          } : ${customerName}</div>
        </div>
      `);

      // Order details
      orderListHTML.push(`
        <div class="order-section">
          <div class="order-details">
            ${
              contactNumber !== "N/A"
                ? `<div class="info-line">Contact Number : ${contactNumber}</div>`
                : ""
            }
            ${
              order.orderTakenBy?.displayName
                ? `<div class="info-line">Agent : ${order.orderTakenBy.displayName}</div>`
                : ""
            }
            <br/>
          </div>
          <div class="thin-divider"></div>
        </div>
      `);

      // Order items
      orderListHTML.push(`
        <div class="items-section">
          <div class="items-header">ORDER ITEMS</div>
          <div class="thin-divider"></div>
      `);

      order.orderItems.forEach((item, idx) => {
        const itemTotal = calculateItemTotal(item);
        const hasDiscount = (item.perItemManualDiscount || 0) > 0;
        const originalTotal = (item.unitPrice || 0) * (item.quantity || 0);
        const itemTinyId = getItemTinyId(item);
        const tinyIdBadge = itemTinyId ? 
          `<span class="tiny-id">(${itemTinyId})</span>` : "";

        orderListHTML.push(`
          <div class="item-section">
            <div class="item-details-row">
              <div class="item-name">${idx + 1}. ${item.displayName}
                ${tinyIdBadge} -
              </div>
              <div class="item-quantity-price">${item.quantity}</div>
            </div>
            ${
              hasDiscount
                ? `<div class="item-discount-row">Discount: Rs.${item.perItemManualDiscount} per item</div>`
                : ""
            }
            <div class="item-total-row">
              ${
                hasDiscount
                  ? `<span class="original-price">Rs.${originalTotal.toFixed(
                      2
                    )}</span>`
                  : ""
              }
              <span class="final-price">Rs.${itemTotal.toFixed(2)}</span>
            </div>
            
            ${
              idx < order.orderItems.length - 1
                ? `<div class="dotted-divider"></div>`
                : ""
            }
          </div>
        `);
      });

      orderListHTML.push(`</div>`);
      orderListHTML.push(`<div class="divider"></div>`);

      // Add order summary for rider copy
      const paymentSummary = order.paymentSummary;
      const paymentStatusHTML = order.paymentCompleted
        ? `<div class="summary-line paid-status">PAID</div>`
        : `<div class="summary-line unpaid-status">PENDING PAYMENT</div>`;

      // Calculate total discount
      const totalItemDiscount = order.orderItems.reduce((sum, item) => {
        return sum + (item.perItemManualDiscount || 0) * (item.quantity || 0);
      }, 0);

      // Calculate additional discount from payment summary
      const additionalDiscount =
        (paymentSummary?.discount || 0) - totalItemDiscount;

      orderListHTML.push(`
        <div class="order-summary">
          <div class="summary-line">Sub Total: Rs.${(
            paymentSummary?.totalPrice || order.totalPrice
          ).toFixed(2)}</div>
          ${
            totalItemDiscount > 0
              ? `<div class="summary-line">Item Discounts: Rs.${totalItemDiscount.toFixed(
                  2
                )}</div>`
              : ""
          }
          ${
            additionalDiscount > 0
              ? `<div class="summary-line">Additional Discount: Rs.${additionalDiscount.toFixed(
                  2
                )}</div>`
              : additionalDiscount < 0
              ? `<div class="summary-line">Surcharge: Rs.${Math.abs(
                  additionalDiscount
                ).toFixed(2)}</div>`
              : ""
          }
          ${
            order.isDeliveryOrder && paymentSummary?.deliveryFee
              ? `<div class="summary-line">${
                  paymentSummary.isDeliveryFree
                    ? "Delivery Fee (FREE)"
                    : "Delivery Fee"
                }: Rs.${paymentSummary.deliveryFee.toFixed(2)}</div>`
              : ""
          }
          <div class="summary-line total-amount">Total Payable: Rs.${(
            paymentSummary?.netAmount || order.totalPrice
          ).toFixed(2)}</div>
          <div class="summary-line">Payment Method: ${paymentMethod}</div>
          ${paymentStatusHTML}
        </div>
      `);

      orderListHTML.push(`<div class="divider"></div>`);

      // Add order / packing / payment notes
      orderListHTML.push(generateOrderNotesHTML(order, "rider"));
      orderListHTML.push(`<div class="divider"></div>`);

      // Add packing list here (after order summary, before footer)
      orderListHTML.push(generatePackingListHTML(order, "rider"));
      orderListHTML.push(`<div class="divider"></div>`);

      // Add rider footer
      orderListHTML.push(`
        <div class="footer">
          <div>THANK YOU FOR YOUR ORDER</div>
          <div>SAFE RIDE</div>
        </div>
      `);
    } else {
      // CUSTOMER COPY
      orderListHTML.push(`
        <div class="header">
          <div class="logo-container">
            <img src="${LOGO_URL}" alt="logo" class="logo-image" />
          </div>
          <div class="header-title">ORDER BILL</div>
          <div class="header-subtitle">CUSTOMER COPY</div>
        </div>
        
        <div class="order-ref-with-bg">ORDER REFERENCE : ${orderReference}</div>
        
        <div class="info-section">
          ${
            order.tinyId
              ? `<div class="info-line">Order Tiny ID: <span class="tiny-id-header">(${order.tinyId})</span></div>`
              : ""
          }
          <div class="info-line">Date : ${dateStr}</div>
          <div class="info-line">Time : ${timeStr}</div>
          ${
            order.cart?.displayName
              ? `<div class="info-line">Cart : ${order.cart.displayName}</div>`
              : ""
          }
        </div>
        
        <div class="divider"></div>
      `);

      // Customer information
      orderListHTML.push(`
        <div class="group-section">
          <div class="group-header">${
            isOrganizationOrder ? "ORGANIZATION" : "CUSTOMER"
          } : ${customerName}</div>
        </div>
      `);

      // Order details
      orderListHTML.push(`
        <div class="order-section">
          <div class="order-details">
            ${
              contactNumber !== "N/A"
                ? `<div class="info-line">Contact Number : ${contactNumber}</div>`
                : ""
            }
            ${
              order.orderTakenBy?.displayName
                ? `<div class="info-line">Agent : ${order.orderTakenBy.displayName}</div>`
                : ""
            }
          <br/>
          </div>
          <div class="thin-divider"></div>
        </div>
      `);

        const isCustomerPandS = order.organization?.displayName?.includes('P&S') ?? false;

        if (!isCustomerPandS) {
          orderListHTML.push(`
            <div class="items-section">
              <div class="items-header">ORDER ITEMS</div>
              <div class="thin-divider"></div>
          `);

          order.orderItems.forEach((item, idx) => {
            const itemTotal = calculateItemTotal(item);
            const hasDiscount = (item.perItemManualDiscount || 0) > 0;
            const originalTotal = (item.unitPrice || 0) * (item.quantity || 0);
            const itemTinyId = getItemTinyId(item);
            const tinyIdBadge = itemTinyId ?
              `<span class="tiny-id">(${itemTinyId})</span>` : "";

            orderListHTML.push(`
              <div class="item-section">
                <div class="item-details-row">
                  <div class="item-name">${idx + 1}. ${item.displayName}
                    ${tinyIdBadge} -
                  </div>
                  <div class="item-quantity-price">${item.quantity}</div>
                </div>
                ${
                  hasDiscount
                    ? `<div class="item-discount-row">Discount: Rs.${item.perItemManualDiscount} per item</div>`
                    : ""
                }
                <div class="item-total-row">
                  ${
                    hasDiscount
                      ? `<span class="original-price">Rs.${originalTotal.toFixed(
                          2
                        )}</span>`
                      : ""
                  }
                  <span class="final-price">Rs.${itemTotal.toFixed(2)}</span>
                </div>

                ${
                  idx < order.orderItems.length - 1
                    ? `<div class="dotted-divider"></div>`
                    : ""
                }
              </div>
            `);
          });

          orderListHTML.push(`</div>`);
          orderListHTML.push(`<div class="divider"></div>`);

          // Add order summary for customer copy
          const paymentSummary = order.paymentSummary;
          const paymentStatusHTML = order.paymentCompleted
            ? `<div class="summary-line paid-status">PAID</div>`
            : `<div class="summary-line unpaid-status">PENDING PAYMENT</div>`;

          // Calculate total discount
          const totalItemDiscount = order.orderItems.reduce((sum, item) => {
            return sum + (item.perItemManualDiscount || 0) * (item.quantity || 0);
          }, 0);

          // Calculate additional discount from payment summary
          const additionalDiscount =
            (paymentSummary?.discount || 0) - totalItemDiscount;

          orderListHTML.push(`
            <div class="order-summary">
              <div class="summary-line">Sub Total: Rs.${(
                paymentSummary?.totalPrice || order.totalPrice
              ).toFixed(2)}</div>
              ${
                totalItemDiscount > 0
                  ? `<div class="summary-line">Item Discounts: Rs.${totalItemDiscount.toFixed(
                      2
                    )}</div>`
                  : ""
              }
              ${
                additionalDiscount > 0
                  ? `<div class="summary-line">Additional Discount: Rs.${additionalDiscount.toFixed(
                      2
                    )}</div>`
                  : additionalDiscount < 0
                  ? `<div class="summary-line">Surcharge: Rs.${Math.abs(
                      additionalDiscount
                    ).toFixed(2)}</div>`
                  : ""
              }
              ${
                order.isDeliveryOrder && paymentSummary?.deliveryFee
                  ? `<div class="summary-line">${
                      paymentSummary.isDeliveryFree
                        ? "Delivery Fee (FREE)"
                        : "Delivery Fee"
                    }: Rs.${paymentSummary.deliveryFee.toFixed(2)}</div>`
                  : ""
              }
              <div class="summary-line total-amount">Total Payable: Rs.${(
                paymentSummary?.netAmount || order.totalPrice
              ).toFixed(2)}</div>
              <div class="summary-line">Payment Method: ${paymentMethod}</div>
              ${paymentStatusHTML}
            </div>
          `);

          orderListHTML.push(`<div class="divider"></div>`);
      }

      // Add order / packing / payment notes (skipped entirely for P&S customer copies)
      if (!isCustomerPandS) {
        orderListHTML.push(generateOrderNotesHTML(order, "customer"));
        orderListHTML.push(`<div class="divider"></div>`);
      }

        // Add packing list for customer copy
        orderListHTML.push(generatePackingListHTML(order, "customer"));

        if (isCustomerPandS) {
          const totalPackQty = order.orderItems.reduce((sum, item) => sum + item.quantity, 0);
          orderListHTML.push(`
            <div class="thin-divider"></div>
            <div class="pands-total-qty">Total Pack Qty: ${totalPackQty}</div>
            <div class="pands-signature-item">
              <span class="signature-label">Issued By :   _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _</span>
            </div>
            <div style="height:4mm;"></div>
            <div class="pands-signature-item">
              <span class="signature-label">Received By : _ _ _ _ _ _ _ _ _ _ _ _ _ _ _</span>
            </div>
            <div style="height:2mm;"></div>
          `);
        }

        orderListHTML.push(`<div class="divider"></div>`);

      // Add customer footer
      orderListHTML.push(`
        <div class="footer">
          <div>THANK YOU FOR YOUR ORDER</div>
          
          <div>For inquiries: 011 444 1333</div>
        </div>
        <div class="cutting-line"></div>
        <div class="footer-spacing"></div>
      `);
    }

    // Return the HTML structure with updated CSS
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Order ${
    copyType === "rider" ? "Rider" : "Customer"
  } Copy - ${orderReference}</title>
  <style>
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  
  @page {
    size: 80mm auto;
    margin: 0;
    padding: 0;
  }
  
  html, body {
    direction: ltr !important;
    unicode-bidi: normal !important;
    text-align: left !important;
  }
  
  @media print {
    html, body {
      width: 80mm !important;
      min-width: 80mm !important;
      max-width: 80mm !important;
      height: auto !important;
      margin: 0 auto !important;
      padding: 0 !important;
      font-family: 'Arial Narrow', Arial, sans-serif !important;
      font-size: 10pt !important;
      line-height: 1.2 !important;
      background: white !important;
      color: black !important;
      direction: ltr !important;
      overflow: visible !important;
      display: block !important;
      position: relative !important;
      left: 0 !important;
      right: 0 !important;
    }
    
    .thermal-content {
      width: 76mm !important;
      min-width: 76mm !important;
      max-width: 76mm !important;
      height: auto !important;
      margin: 0 auto !important;
      padding: 1mm 2mm !important;
      display: block !important;
      position: relative !important;
      left: 0 !important;
      right: 0 !important;
    }
    
    .content-wrapper {
      width: 100% !important;
      display: block !important;
      margin: 0 auto !important;
    }
    
    .no-print { 
      display: none !important; 
    }
  }
  
  body {
    font-family: 'Arial Narrow', Arial, sans-serif;
    font-size: 10pt;
    line-height: 1.2;
    width: 80mm;
    min-width: 80mm;
    max-width: 80mm;
    height: auto;
    margin: 0 auto;
    padding: 0;
    background: white;
    color: black;
    overflow: visible;
    display: block;
    direction: ltr;
    text-align: left;
  }
  
  .thermal-content {
    width: 76mm;
    min-width: 76mm;
    max-width: 76mm;
    height: auto;
    margin: 0 auto;
    padding: 1mm 2mm;
    display: block;
    direction: ltr;
    text-align: left;
  }
  
  .content-wrapper {
    width: 100%;
    display: block;
    margin: 0 auto;
  }
  
  /* Tiny ID styles - Updated */
  .tiny-id {
    font-size: 9pt !important;
    font-weight: bold !important;
    color: #000000 !important;
    margin-left: 2mm;
    margin-right: 1mm;
    display: inline-block !important;
  }
  
  .tiny-id-header {
    font-size: 9.5pt !important;
    font-weight: bold !important;
    color: #000000 !important;
    margin-left: 1mm;
    display: inline-block !important;
  }

  .header {
    text-align: center;
    margin: 0 0 2mm 0;
    padding: 0;
    width: 100%;
  }
  
  .logo-container {
    margin: 0 0 1mm 0;
    text-align: center;
    width: 100%;
  }
  
  .logo-image {
    max-width: 25mm;
    max-height: 12mm;
    height: auto;
    display: inline-block;
  }
  
  .header-title {
    font-weight: bold;
    font-size: 11pt;
    margin: 0 0 0.5mm 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-align: center;
    width: 100%;
  }
  
  .header-subtitle {
    font-weight: bold;
    font-size: 9pt;
    margin: 0 0 1mm 0;
    text-transform: uppercase;
    text-align: center;
    width: 100%;
  }
  
  .info-section {
    margin: 1mm 0;
    width: 100%;
  }
  
  .info-line {
    font-size: 8pt;
    margin: 0.5mm 0;
    font-weight: normal;
    text-align: left;
    width: 100%;
  }
  
  .divider {
    border-top: 1px solid #000;
    margin: 2mm 0;
    height: 0;
    width: 100%;
  }
  
  .thin-divider {
    border-top: 0.5px solid #666;
    margin: 1mm 0;
    height: 0;
    width: 100%;
  }
  
  .dotted-divider {
    border-top: 1px dotted #999;
    margin: 1mm 0;
    height: 0;
    width: 100%;
  }
  
  .cutting-line {
    border-top: 2px dashed #000;
    margin: 3mm 0;
    height: 0;
    width: 100%;
  }
  
  .group-section {
    margin: 2mm 0;
    width: 100%;
  }
  
  .order-section {
    margin: 1mm 0;
    width: 100%;
  }
  
  .item-section {
    margin: 0.5mm 0;
    width: 100%;
  }
  
  .items-section {
    margin: 1mm 0;
    width: 100%;
  }
  
  .items-header {
    font-weight: bold;
    font-size: 9pt;
    margin: 0.5mm 0;
    text-align: center;
    text-transform: uppercase;
    width: 100%;
  }
  
  .order-spacing {
    height: 1mm;
    width: 100%;
  }
  
  .group-header {
    font-weight: bold;
    font-size: 9pt;
    margin: 0.3mm 0;
    padding: 0;
    text-align: left;
    text-transform: uppercase;
    width: 100%;
  }
  
  .order-ref-with-bg {
    font-weight: bold;
    font-size: 9pt;
    margin: 0.5mm 0;
    padding: 1mm;
    text-align: left;
    text-transform: uppercase;
    background-color: black;
    color: #fff;
    border: 0.5px solid #ddd;
    width: 100%;
  }
  
  .order-details {
    margin: 0.5mm 0 1mm 0;
    width: 100%;
  }
  
  .item-name {
    font-size: 9pt;
    margin: 0.3mm 0;
    font-weight: bold;
    text-align: left;
    width: 100%;
    line-height: 1.3;
  }
  
  .item-details-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 0.3mm 0;
    width: 100%;
  }
  
  .item-quantity-price {
    font-size: 8pt;
    font-weight: bold;
    text-align: right;
    min-width: 5mm;
  }
  
  .item-discount-row {
    font-size: 7pt;
    margin: 0.1mm 0 0 1mm;
    font-style: italic;
    color: #28a745;
    text-align: left;
    width: 100%;
  }
  
  .item-total-row {
    margin: 0.3mm 0 0 1mm;
    text-align: right;
    width: 100%;
  }
  
  .original-price {
    font-size: 8pt;
    text-decoration: line-through;
    color: #999;
    margin-right: 3mm;
  }
  
  .final-price {
    font-size: 9pt;
    font-weight: bold;
    color: #000;
  }
  
  /* Item quantity style */
  .item-quantity {
    font-size: 8pt;
    font-weight: bold;
    margin-left: 2mm;
    color: #000;
  }
  
  .custom-item-section {
    margin: 0.5mm 0 0.5mm 2mm;
    padding: 0.3mm 0;
    font-size: 8pt;
    width: 100%;
  }
  
  .custom-item-details-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 0.2mm 0;
    width: 100%;
  }
  
  .custom-item-name {
    font-size: 8pt;
    text-align: left;
    flex: 1;
    color: #666;
    line-height: 1.3;
  }
  
  .custom-item-quantity {
    font-size: 8pt;
    text-align: right;
    min-width: 5mm;
    color: #666;
  }
  
  /* Packing List Styles - Updated */
  .packing-section {
    margin: 2mm 0;
    padding: 1mm;
    border: 0.5px solid #dee2e6;
    width: 100%;
  }
  
  .packing-header {
    font-weight: bold;
    font-size: 9pt;
    text-align: center;
    text-transform: uppercase;
    margin-bottom: 1mm;
    color: #495057;
    width: 100%;
  }
  
  .packing-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 1mm 0;
    padding: 0.5mm 0;
    width: 100%;
  }
  
  .packing-item-no-checkbox {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 1mm 0;
    padding: 0.5mm 0;
    width: 100%;
  }
  
  .custom-packing-item {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 0.5mm 0 0.5mm 2mm;
    padding: 0.3mm 0;
    font-size: 8pt;
    width: 100%;
  }
  
  .custom-packing-item-no-checkbox {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin: 0.5mm 0 0.5mm 2mm;
    padding: 0.3mm 0;
    font-size: 8pt;
    width: 100%;
  }
  
  .addon-separator {
    margin: 0.5mm 0 0.5mm 2mm;
    padding: 0.2mm 0;
    font-size: 7pt;
    width: 100%;
  }
  
  .addon-header {
    font-weight: bold;
    font-style: italic;
    color: black;
  }
  
  .addon-item {
    margin-left: 4mm !important;
  }
  
  .packing-details {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex: 1;
    margin-right: 2mm;
  }
  
  .custom-packing-details {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex: 1;
    margin-right: 2mm;
  }
  
  .packing-name {
    font-size: 9pt;
    font-weight: bold;
    text-align: left;
    flex: 1;
    line-height: 1.3;
  }
  
  .custom-packing-name {
    font-size: 8pt;
    text-align: left;
    flex: 1;
    color:black;
    line-height: 1.3;
    white-space: normal;
    word-break: break-word;
  }
  
  /* Remove the separate quantity column styles */
  .packing-qty {
    display: none;
  }
  
  .custom-packing-qty {
    display: none;
  }
  
  .checkbox {
    width: 3mm;
    height: 3mm;
    border: 1px solid #000;
    margin-left: 1mm;
    flex-shrink: 0;
  }
  
  .custom-checkbox {
    width: 2.5mm;
    height: 2.5mm;
    border: 1px solid #666;
    margin-left: 1mm;
    flex-shrink: 0;
  }
  
  .order-summary {
    margin: 1mm 0;
    padding: 1mm;
    background-color: #f5f5f5;
    border: 0.5px solid #ddd;
    width: 100%;
  }
  
  .summary-line {
    font-size: 8pt;
    margin: 0.3mm 0;
    font-weight: bold;
    text-align: right;
    width: 100%;
  }
  
  .total-amount {
    font-size: 9pt;
    font-weight: bolder;
    color: #000;
    border-top: 0.5px solid #ddd;
    padding-top: 0.5mm;
    margin-top: 0.5mm;
  }
  
  .paid-status {
    color: #28a745;
    font-weight: bold;
    text-align: center;
    margin-top: 1mm;
    border-top: 0.5px solid #ddd;
    padding-top: 0.5mm;
    width: 100%;
  }
  
  .unpaid-status {
    color: #dc3545;
    font-weight: bold;
    text-align: center;
    margin-top: 1mm;
    border-top: 0.5px solid #ddd;
    padding-top: 0.5mm;
    width: 100%;
  }
  
  .notes-section {
    margin: 1mm 0;
    padding: 1mm;
    width: 100%;
  }
  
  .notes-header {
    font-size: 8pt;
    font-weight: bold;
    color:black;
    margin-bottom: 0.5mm;
  }
  
  .notes-content {
    font-size: 8pt;
    font-style: italic;
    color: #333;
  }

  .order-note-section {
    margin: 1mm 0;
    padding: 1mm;
    border: 0.5px dashed #888;
    width: 100%;
  }

  .order-note-label {
    font-size: 7.5pt;
    font-weight: bold;
    text-align: left;
    margin-bottom: 0.3mm;
    text-transform: uppercase;
  }

  .order-note-text {
    font-size: 7.5pt;
    font-weight: normal;
    text-align: left;
    word-wrap: break-word;
    word-break: break-word;
  }

  .payment-note-section {
    border-style: solid;
    border-color: #333;
  }
  
  .footer {
    text-align: center;
    font-size: 8pt;
    margin: 3mm 0 0 0;
    padding: 1mm 0 0 0;
    border-top: 1px solid #000;
    font-weight: bold;
    width: 100%;
  }
  
  .footer-spacing {
    height: 5mm;
    margin: 2mm 0;
    width: 100%;
  }

  .pands-total-qty {
      font-size: 9pt;
      font-weight: normal;
      text-align: left;
      margin-top: 2mm;
      margin-bottom: 4mm;
    }

    .pands-signature-item {
      display: flex;
      align-items: center;
      width: 100%;
    }

    .signature-label {
      font-size: 8pt;
      font-weight: normal;
    }
  
  @media print {
    .footer-spacing {
      height: 5mm !important;
      margin: 2mm 0 !important;
    }
    
    .cutting-line {
      border-top: 2px dashed #000 !important;
      margin: 3mm 0 !important;
    }
  }
</style>
</head>
<body>
  <div class="content-wrapper">
    <div class="thermal-content">
      ${orderListHTML.join("")}
    </div>
  </div>
</body>
</html>
    `;
  };

  // Print single order
  const printOrder = async ({
    orderId,
    orderReference,
    copyType,
  }: PrintByOrderOptions): Promise<void> => {
    setIsPrinting(true);
    try {
      // Find the order in orders
      const order = orders?.find((o) => o.id === orderId);

      if (!order) {
        console.warn(`Order not found: ${orderId}`);
        toast.error("Order not found");
        return;
      }

      const htmlContent = generatePrintHTMLForOrder(
        order,
        orderReference,
        copyType
      );

      const blob = new Blob([htmlContent], { type: "text/html" });
      const url = URL.createObjectURL(blob);

      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "none";
      iframe.style.opacity = "0";
      iframe.style.top = "-9999px";
      iframe.style.left = "-9999px";
      iframe.id = "thermal-print-frame";

      iframe.onload = () => {
        setTimeout(() => {
          try {
            if (iframe.contentWindow) {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();

              const cleanup = () => {
                setTimeout(() => {
                  if (document.body.contains(iframe)) {
                    document.body.removeChild(iframe);
                  }
                  URL.revokeObjectURL(url);
                }, 100);
              };

              iframe.contentWindow.addEventListener("afterprint", cleanup);

              setTimeout(() => {
                if (document.body.contains(iframe)) {
                  document.body.removeChild(iframe);
                  URL.revokeObjectURL(url);
                }
              }, 5000);
            }
          } catch (error) {
            console.error("Print error:", error);
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
            URL.revokeObjectURL(url);
          }
        }, 500);
      };

      iframe.src = url;
      document.body.appendChild(iframe);
    } catch (error) {
      console.error("Error printing order:", error);
      toast.error("Failed to print order");
      throw error;
    } finally {
      setIsPrinting(false);
    }
  };

  // Print multiple orders
  const printMultipleOrders = async (
    ordersToPrint: PrintByOrderOptions[]
  ): Promise<void> => {
    setIsPrinting(true);
    try {
      for (const orderOptions of ordersToPrint) {
        await printOrder(orderOptions);
        // Add a small delay between prints to ensure proper sequencing
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    } catch (error) {
      console.error("Error printing multiple orders:", error);
      toast.error("Failed to print orders");
      throw error;
    } finally {
      setIsPrinting(false);
    }
  };

  // Print both copies (rider and customer)
  const printBothCopies = async (
    orderId: string,
    orderReference: string
  ): Promise<void> => {
    setIsPrinting(true);
    try {
      await printOrder({ orderId, orderReference, copyType: "rider" });
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await printOrder({ orderId, orderReference, copyType: "customer" });
    } catch (error) {
      console.error("Error printing both copies:", error);
      throw error;
    } finally {
      setIsPrinting(false);
    }
  };

  const showPreview = (
    orderId: string,
    copyType: "rider" | "customer" = "rider"
  ): void => {
    const order = orders?.find((o) => o.id === orderId);
    if (!order) {
      toast.error("Order not found for preview");
      return;
    }

    const htmlContent = generatePrintHTMLForOrder(
      order,
      order.orderReference,
      copyType
    );
    setPreviewHTML(htmlContent);
    setPreviewVisible(true);
  };

  const hidePreview = (): void => {
    setPreviewVisible(false);
    setPreviewHTML("");
  };

  // Get all available orders
  const getAllAvailableOrders = (): Order[] => {
    return [...orders];
  };

  return {
    // Order printing functions
    printOrder,
    printMultipleOrders,
    printBothCopies,

    // State and utility functions
    isPrinting,
    previewVisible,
    previewHTML,
    showPreview,
    hidePreview,

    // Data availability indicators
    hasOrders: orders.length > 0,
    availableOrders: getAllAvailableOrders(),
  };
};

export default useOrderPrint;